import React from 'react';
import { LightbulbIcon, MessageSquareIcon, AlertCircleIcon, CopyIcon, ThumbsUpIcon, ThumbsDownIcon } from 'lucide-react';
import { useClosingAssistant } from '../../contexts/ClosingAssistantContext';
export const ClosingSuggestionsPanel: React.FC = () => {
  const {
    customerProfile,
    closingReadiness
  } = useClosingAssistant();
  // Generate closing techniques based on customer profile and readiness
  const getClosingTechniques = () => {
    if (closingReadiness === 'high') {
      return [{
        title: 'Assumptive Close',
        description: "Proceed as if the sale is already made: 'Let's get you booked on the July 15th departure.'",
        suitable: true
      }, {
        title: 'Urgency Close',
        description: "Emphasize limited availability: 'We only have 3 spots left on this departure date.'",
        suitable: true
      }, {
        title: 'Summary Close',
        description: "Recap all benefits and ask for the booking: 'As you can see, this package offers everything you're looking for...'",
        suitable: true
      }];
    } else if (closingReadiness === 'medium') {
      return [{
        title: 'Alternative Choice Close',
        description: "Offer choices that both lead to a sale: 'Would you prefer the July or August departure?'",
        suitable: true
      }, {
        title: 'Question Close',
        description: "Ask questions that lead to a yes: 'If we could guarantee the window seat, would you be ready to book?'",
        suitable: true
      }, {
        title: 'Assumptive Close',
        description: 'Proceed as if the sale is already made',
        suitable: false
      }];
    } else {
      return [{
        title: 'Soft Trial Close',
        description: "Test readiness without pressure: 'How does this itinerary sound so far?'",
        suitable: true
      }, {
        title: 'Information Close',
        description: "Offer additional information: 'Would you like me to send you a detailed itinerary?'",
        suitable: true
      }, {
        title: 'Urgency Close',
        description: 'Emphasize limited availability',
        suitable: false
      }];
    }
  };
  // Generate objection handlers based on potential objections
  const getObjectionHandlers = () => {
    return [{
      objection: "It's too expensive",
      response: "I understand budget concerns. Let me show you the value breakdown and what's included that other packages don't offer."
    }, {
      objection: 'Need to think about it',
      response: "Of course, it's a significant decision. What specific aspects would you like more information about to help with your decision?"
    }, {
      objection: 'Need to check with spouse/partner',
      response: 'I completely understand. Would it help if I sent you a detailed summary that you could share with them?'
    }];
  };
  // Get alternative offers based on customer profile
  const getAlternativeOffers = () => {
    if (customerProfile.priceLevel === 'budget') {
      return ["Offer the 'Early Bird' discount if they book today", 'Suggest a similar package with fewer included activities to reduce cost', 'Mention the option of a payment plan to spread out the cost'];
    } else if (customerProfile.priceLevel === 'luxury') {
      return ['Offer a complimentary room upgrade if they book today', 'Suggest adding exclusive experiences not available to regular bookings', 'Mention the option of a private tour guide for parts of the journey'];
    } else {
      return ['Offer a 5% discount if they book within 24 hours', 'Suggest adding an optional excursion at a special rate', 'Mention the flexibility to customize parts of the itinerary'];
    }
  };
  const closingTechniques = getClosingTechniques();
  const objectionHandlers = getObjectionHandlers();
  const alternativeOffers = getAlternativeOffers();
  // Simulate copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };
  return <div className="p-4" data-id="element-840">
      <div className="flex items-center mb-4" data-id="element-841">
        <div className="p-2 bg-amber-100 rounded-full mr-3" data-id="element-842">
          <LightbulbIcon className="h-5 w-5 text-amber-600" data-id="element-843" />
        </div>
        <div data-id="element-844">
          <h3 className="font-medium text-gray-800" data-id="element-845">Closing Suggestions</h3>
          <p className="text-xs text-gray-500" data-id="element-846">Personalized sales techniques</p>
        </div>
      </div>
      {/* Recommended Closing Techniques */}
      <div className="mb-4" data-id="element-847">
        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-848">
          Recommended Techniques
        </h4>
        <div className="space-y-2" data-id="element-849">
          {closingTechniques.map((technique, index) => <div key={index} className={`p-3 rounded-lg border ${technique.suitable ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`} data-id="element-850">
              <div className="flex justify-between items-start" data-id="element-851">
                <div className="flex items-center" data-id="element-852">
                  {technique.suitable ? <ThumbsUpIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-853" /> : <ThumbsDownIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-854" />}
                  <h5 className="text-sm font-medium text-gray-800" data-id="element-855">
                    {technique.title}
                  </h5>
                </div>
                {technique.suitable && <button onClick={() => copyToClipboard(technique.description)} className="text-gray-400 hover:text-gray-600" data-id="element-856">
                    <CopyIcon className="h-3 w-3" data-id="element-857" />
                  </button>}
              </div>
              <p className="text-xs text-gray-600 mt-1 ml-6" data-id="element-858">
                {technique.description}
              </p>
            </div>)}
        </div>
      </div>
      {/* Objection Handlers */}
      <div className="mb-4" data-id="element-859">
        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-860">
          Objection Handlers
        </h4>
        <div className="space-y-2" data-id="element-861">
          {objectionHandlers.map((handler, index) => <div key={index} className="p-3 rounded-lg border border-amber-200 bg-amber-50" data-id="element-862">
              <div className="flex justify-between items-start" data-id="element-863">
                <div className="flex items-start" data-id="element-864">
                  <AlertCircleIcon className="h-4 w-4 text-amber-500 mr-2 mt-0.5" data-id="element-865" />
                  <div data-id="element-866">
                    <h5 className="text-sm font-medium text-gray-800" data-id="element-867">
                      "{handler.objection}"
                    </h5>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-868">
                      {handler.response}
                    </p>
                  </div>
                </div>
                <button onClick={() => copyToClipboard(handler.response)} className="text-gray-400 hover:text-gray-600 ml-2" data-id="element-869">
                  <CopyIcon className="h-3 w-3" data-id="element-870" />
                </button>
              </div>
            </div>)}
        </div>
      </div>
      {/* Alternative Offers */}
      <div data-id="element-871">
        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-872">
          Alternative Offers
        </h4>
        <div className="space-y-2" data-id="element-873">
          {alternativeOffers.map((offer, index) => <div key={index} className="p-3 rounded-lg border border-blue-200 bg-blue-50" data-id="element-874">
              <div className="flex items-center" data-id="element-875">
                <MessageSquareIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-876" />
                <p className="text-sm text-gray-700" data-id="element-877">{offer}</p>
              </div>
            </div>)}
        </div>
      </div>
      {/* Quick Action Buttons */}
      <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-878">
        <button className="p-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" data-id="element-879">
          Create Quote
        </button>
        <button className="p-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700" data-id="element-880">
          Book Now
        </button>
      </div>
    </div>;
};